import { useCallback, useEffect, useState } from 'react';

/**
 * A saved tax estimate — a frozen snapshot of the Tax Harvest Estimator at the
 * moment the user clicked "Save estimate to Tax Plan". These values never
 * auto-update when prices change; they capture the scenario as it was saved.
 *
 * All monetary fields are in AUD (the estimator is AUD-denominated).
 */
export interface TaxPlanEstimate {
  id: string;
  coinId: string;
  coinName: string;
  coinTicker: string;          // uppercase symbol, e.g. "BTC"
  coinImage?: string;
  amountToSell: number;        // quantity of coins
  costBasis: number;           // buy price per coin (AUD)
  estimatedSalePrice: number;  // sale price per coin (AUD)
  holdDurationMonths: number;  // months held — for CGT discount eligibility
  cgtDiscountApplied: boolean; // true if held 12+ months (50% CGT discount)
  capitalGain: number;         // total gain/loss across the position (AUD)
  taxBracket: string;          // bracket label, e.g. "$45,001 – $120,000"
  taxBracketRate: number;      // marginal rate as a fraction, e.g. 0.325
  estimatedCGT: number;        // calculated CGT owed (AUD)
  savedAt: string;             // ISO timestamp
  scenarioName: string;        // user label, defaults to "Saved Mon DD, YYYY"
}

export interface TaxPlanSummary {
  totalProceeds: number;       // Σ amountToSell × estimatedSalePrice
  totalCostBasis: number;      // Σ amountToSell × costBasis
  totalGain: number;           // Σ capitalGain
  totalCGT: number;            // Σ estimatedCGT
  count: number;
  discountQualifyingCount: number;
}

const STORAGE_KEY = 'taxPlanEstimates';
// Same-document broadcast so every mounted useTaxPlan instance (e.g. the saved
// list and the harvest panel) stays in sync after add/delete. The native
// `storage` event only fires across tabs, not within the same document.
const SYNC_EVENT = 'taxplan:changed';

function readEstimates(): TaxPlanEstimate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as TaxPlanEstimate[]) : [];
  } catch {
    return [];
  }
}

function writeEstimates(estimates: TaxPlanEstimate[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(estimates));
  } catch {
    /* ignore quota / serialization errors */
  }
  // Notify other hook instances in this document.
  try {
    window.dispatchEvent(new Event(SYNC_EVENT));
  } catch {
    /* ignore */
  }
}

function genId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useTaxPlan() {
  const [estimates, setEstimates] = useState<TaxPlanEstimate[]>(() => readEstimates());

  // Re-sync on cross-instance broadcast and cross-tab storage events.
  useEffect(() => {
    const resync = () => setEstimates(readEstimates());
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === STORAGE_KEY) resync();
    };
    window.addEventListener(SYNC_EVENT, resync);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(SYNC_EVENT, resync);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  /** Append a new frozen estimate. `id` and `savedAt` are generated here. */
  const addEstimate = useCallback(
    (estimate: Omit<TaxPlanEstimate, 'id' | 'savedAt'>): TaxPlanEstimate => {
      const full: TaxPlanEstimate = {
        ...estimate,
        id: genId(),
        savedAt: new Date().toISOString(),
      };
      const next = [...readEstimates(), full];
      writeEstimates(next);
      setEstimates(next);
      return full;
    },
    []
  );

  const deleteEstimate = useCallback((id: string) => {
    const next = readEstimates().filter((e) => e.id !== id);
    writeEstimates(next);
    setEstimates(next);
  }, []);

  const getSummary = useCallback((): TaxPlanSummary => {
    return estimates.reduce<TaxPlanSummary>(
      (acc, e) => {
        acc.totalProceeds += e.amountToSell * e.estimatedSalePrice;
        acc.totalCostBasis += e.amountToSell * e.costBasis;
        acc.totalGain += e.capitalGain;
        acc.totalCGT += e.estimatedCGT;
        acc.count += 1;
        if (e.cgtDiscountApplied) acc.discountQualifyingCount += 1;
        return acc;
      },
      {
        totalProceeds: 0,
        totalCostBasis: 0,
        totalGain: 0,
        totalCGT: 0,
        count: 0,
        discountQualifyingCount: 0,
      }
    );
  }, [estimates]);

  return { estimates, addEstimate, deleteEstimate, getSummary };
}
